import { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { Button } from '@perch/ui';
import { getEmailProvider, getProviderUrl, getProviderLabel, maskEmail } from './emailUtils';
import { posthog } from '../../lib/posthog';

interface CheckEmailScreenProps {
  email: string;
  onBack: () => void;
  onResend: () => Promise<void>;
  onGoogleFallback: () => void;
}

const FALLBACK_DELAY_SECONDS = 60;

export function CheckEmailScreen({ email, onBack, onResend, onGoogleFallback }: CheckEmailScreenProps) {
  const [showFallback, setShowFallback] = useState(false);
  const [resending, setResending] = useState(false);

  const provider = getEmailProvider(email);
  const providerUrl = getProviderUrl(provider);
  const providerLabel = getProviderLabel(provider);
  const masked = maskEmail(email);

  useEffect(() => {
    const timer = setTimeout(() => setShowFallback(true), FALLBACK_DELAY_SECONDS * 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleResend = async () => {
    setResending(true);
    posthog.capture('magic_link_resend');
    await onResend();
    setResending(false);
    setShowFallback(false);
    // Reset the timer by re-mounting — parent handles this by toggling state
  };

  const handleGoogleFallback = () => {
    posthog.capture('magic_link_fallback_google');
    onGoogleFallback();
  };

  return (
    <div className="space-y-6 text-center">
      {/* Animated mail icon */}
      <div className="flex justify-center">
        <div className="animate-[fadeScaleIn_0.4s_ease-out]">
          <Mail className="w-12 h-12 text-stone-400 dark:text-stone-500" strokeWidth={1.5} />
        </div>
      </div>

      <div>
        <h2 className="font-serif text-xl text-stone-900 dark:text-stone-100">
          Check Your Email
        </h2>
        <p className="mt-2 text-sm text-stone-600 dark:text-stone-400">
          We sent a sign-in link to{' '}
          <span className="font-medium text-stone-900 dark:text-stone-100">{masked}</span>
        </p>
      </div>

      {/* Provider-aware smart button */}
      {provider !== 'generic' && providerUrl && (
        <Button
          variant="coral"
          fullWidth
          onClick={() => window.open(providerUrl, '_blank', 'noopener')}
          aria-label={`${providerLabel} in a new tab`}
        >
          {providerLabel}
        </Button>
      )}

      {/* Fallback options — hidden for 60s, then revealed */}
      {showFallback && (
        <div className="space-y-3" aria-live="polite">
          <p className="text-sm text-stone-500 dark:text-stone-400">
            Didn't get it?
          </p>
          <div className="flex items-center justify-center gap-3 text-sm">
            <button
              onClick={handleResend}
              disabled={resending}
              className="text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors disabled:opacity-50"
            >
              {resending ? 'Sending...' : 'Send Again'}
            </button>
            <span className="text-stone-300 dark:text-stone-600">&middot;</span>
            <button
              onClick={handleGoogleFallback}
              className="text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
            >
              Sign In with Google Instead
            </button>
          </div>
          <p className="text-xs text-stone-400 dark:text-stone-500">
            Check your spam folder too.
          </p>
        </div>
      )}

      {/* Back link */}
      <button
        onClick={onBack}
        className="text-sm text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
      >
        &larr; Back to sign in
      </button>
    </div>
  );
}
